import { environment } from '../../environments/environment';

/** M3 ORTP credit-note order type(s) per division. */
export const CREDIT_NOTE_ORDER_TYPE_BY_DIVISION: Record<string, string | readonly string[]> = {
  '100': 'G21',
  '300': 'F21',
  '400': ['N21', 'NT3', 'NT4'],
};

function creditNoteOrderTypesForDivision(division: string): readonly string[] {
  const entry = CREDIT_NOTE_ORDER_TYPE_BY_DIVISION[String(division).trim()];
  if (!entry) {
    return [];
  }
  const list = Array.isArray(entry) ? entry : [entry];
  return list.map((t) => String(t).trim().toUpperCase()).filter(Boolean);
}

export function resolveCreditLimit3(
  source: { okcrl3?: unknown; creditLimit3?: unknown } | null | undefined
): number {
  const raw = source?.okcrl3 ?? source?.creditLimit3;
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return 0;
  }
  const n = Number(raw);
  if (Number.isFinite(n) && n >= 0) {
    return n;
  }
  return 0;
}

/** Live M3 credit from SearchCustomer/Details (same mapping as Orders1 customer search). */
export function applyCustomerCreditFromDetails(
  details: {
    outstanding?: unknown;
    totalOverdue?: unknown;
    okcrl3?: unknown;
    odud?: unknown;
    nood?: unknown;
  } | null | undefined
): {
  outstanding: number;
  totalOverdue: number;
  okcrl3?: string | number;
  creditLimit3: number;
  odud: number;
  nood: number;
} {
  const outstanding = Number(details?.outstanding) || 0;
  const totalOverdue = Number(details?.totalOverdue) || 0;
  const rawOkcrl3 = details?.okcrl3;
  const okcrl3: string | number | undefined =
    typeof rawOkcrl3 === 'string' || typeof rawOkcrl3 === 'number' ? rawOkcrl3 : undefined;
  const creditLimit3 = resolveCreditLimit3({ okcrl3, creditLimit3: undefined });
  const odudNum = Number(details?.odud);
  const odud = Number.isFinite(odudNum) ? odudNum : 0;
  const nood = Number(details?.nood) || 0;
  return { outstanding, totalOverdue, okcrl3, creditLimit3, odud, nood };
}

export interface CreditExposureResult {
  outstanding: number;
  orderValue: number;
  /** outstanding + orderValue */
  exposureTotal: number;
  creditLimit3: number;
  /** exposureTotal - creditLimit3 when over limit, else 0 */
  exceedBy: number;
  exceedsLimit: boolean;
}

export interface CreditLimitDialogData {
  outstanding: number;
  orderValue: number;
  exposureTotal: number;
  creditLimit: number;
  exceedBy: number;
}

export type CreditRestrictionKind = 'none' | 'overdue' | 'exposure' | 'both';
export type CreditLimitDialogKind = 'overdue' | 'exposure' | 'both';

export interface CreditRestrictionResult {
  kind: CreditRestrictionKind;
  /** ODIN amount (informational; Gate 1 no longer uses this). */
  totalOverdue: number;
  /** NOOD — actual overdue count/days from GetCCUCRL. */
  nood: number;
  /** ODUD — Credit Limit 4 threshold days from GetFinancial. */
  odud: number;
  exposure: CreditExposureResult | null;
}

export function isCreditLimitCheckEnabledForDivision(divisionId: string): boolean {
  const allowedDivisions = (environment.creditLimitWarningDivisions || '')
    .split(',')
    .map((d) => d.trim())
    .filter(Boolean);
  return divisionId !== '' && allowedDivisions.includes(divisionId);
}

export function normalizeEmail(email: string): string {
  return String(email ?? '').trim().toLowerCase();
}

/** Parse email from a localStorage value (plain string or JSON-encoded by InMemoryStorage). */
function readEmailFromLocalStorageKey(key: string): string {
  if (typeof localStorage === 'undefined') {
    return '';
  }
  const raw = localStorage.getItem(key);
  if (raw == null) {
    return '';
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed === 'string' && parsed.trim()) {
      return parsed.trim();
    }
  } catch {
    // plain string, not JSON — use as-is
  }
  return trimmed;
}

function readEmailFromUserProfileRaw(raw: string | null): string {
  if (!raw?.trim()) {
    return '';
  }
  try {
    let parsed: unknown = JSON.parse(raw.trim());
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    if (parsed && typeof parsed === 'object' && 'email' in (parsed as object)) {
      const profileEmail = (parsed as { email?: unknown }).email;
      if (typeof profileEmail === 'string' && profileEmail.trim()) {
        return profileEmail.trim();
      }
    }
  } catch {
    // ignore
  }
  return '';
}

/** Current user email for credit-limit soft bypass (localStorage `email` first). */
export function resolveUserEmail(storage: { get: (key: string) => unknown } | null | undefined): string {
  for (const key of ['email', 'userEmail', 'user_email']) {
    const fromLs = readEmailFromLocalStorageKey(key);
    if (fromLs) {
      return fromLs;
    }
  }

  if (typeof localStorage !== 'undefined') {
    const fromProfileLs = readEmailFromUserProfileRaw(localStorage.getItem('userProfile'));
    if (fromProfileLs) {
      return fromProfileLs;
    }
  }

  if (!storage) {
    return '';
  }
  const direct = storage.get('email');
  if (typeof direct === 'string' && direct.trim()) {
    return direct.trim();
  }
  try {
    const profileRaw = storage.get('userProfile');
    const fromProfile =
      typeof profileRaw === 'string'
        ? readEmailFromUserProfileRaw(profileRaw)
        : profileRaw && typeof profileRaw === 'object' && 'email' in (profileRaw as object)
          ? String((profileRaw as { email?: unknown }).email ?? '').trim()
          : '';
    if (fromProfile) {
      return fromProfile;
    }
  } catch {
    // ignore invalid profile JSON
  }
  const userName = storage.get('userName');
  if (typeof userName === 'string' && userName.includes('@')) {
    return userName.trim();
  }
  return '';
}

export type CreditLimitPageAction = 'none' | 'proceed' | 'block';

/** Which flow may use env soft-bypass allowlist (picklist vs sync are separate per environment). */
export type CreditLimitBypassAction = 'picklist' | 'sync';

type CreditLimitEnvMap = Record<string, string | undefined>;

function readCreditLimitEnvList(baseKey: string, divisionId?: string): string {
  const env = environment as unknown as CreditLimitEnvMap;
  const d = String(divisionId ?? '').trim();
  if (d) {
    const suffixed = env[`${baseKey}_${d}`];
    if (suffixed != null && String(suffixed).trim()) {
      return String(suffixed);
    }
  }
  return String(env[baseKey] ?? '');
}

function getCreditLimitSoftBypassAllowlist(
  action: CreditLimitBypassAction,
  divisionId?: string
): string[] {
  const env = environment as {
    creditLimitSoftBypassEmails?: string;
  };
  const baseKey =
    action === 'picklist' ? 'creditLimitSoftBypassPicklistEmails' : 'creditLimitSoftBypassSyncEmails';
  const raw =
    readCreditLimitEnvList(baseKey, divisionId) || env.creditLimitSoftBypassEmails || '';
  return String(raw)
    .split(',')
    .map((e) => normalizeEmail(e))
    .filter(Boolean);
}

/** Picklist credit is unrestricted for 100, 300, and 400 (Save / Sync / Invoice still gated). */
export function shouldSkipCreditLimitOnPicklist(divisionId: string): boolean {
  const d = String(divisionId ?? '').trim();
  return d === '100' || d === '300' || d === '400';
}

function parseCreditLimitContactEmails(raw: string): string[] {
  return String(raw ?? '')
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);
}

/** Shown on hard-block dialogs when the logged-in user is not on the bypass allowlist. */
export function getCreditLimitBypassContactNote(
  action: CreditLimitBypassAction,
  divisionId?: string
): string {
  const baseKey =
    action === 'picklist' ? 'creditLimitBypassContactPicklist' : 'creditLimitBypassContactSync';
  const emails = parseCreditLimitContactEmails(readCreditLimitEnvList(baseKey, divisionId).trim());
  if (emails.length === 0) {
    return '';
  }
  const intro =
    action === 'picklist'
      ? 'To create a picklist when over limit, contact:'
      : 'To sync when over limit, contact:';
  return `${intro} ${emails.join(' ')}`;
}

/** Credit Limit 4: block when NOOD >= ODUD; skip check when ODUD is 0/missing. */
export function hasOverdueDaysRestriction(nood: unknown, odud: unknown): boolean {
  const limit = Number(odud);
  if (!Number.isFinite(limit) || limit <= 0) {
    return false;
  }
  const days = Number(nood) || 0;
  return days >= limit;
}

/** @deprecated Use hasOverdueDaysRestriction (CL4 days). Amount gate removed. */
export function hasOverdueRestriction(totalOverdue: unknown): boolean {
  return (Number(totalOverdue) || 0) > 0;
}

export function resolveCreditRestrictionFromState(
  outstanding: unknown,
  orderValue: unknown,
  creditLimit3: unknown,
  nood: unknown,
  odud: unknown,
  totalOverdue: unknown = 0
): CreditRestrictionResult {
  const overdueParsed = round2(Number(totalOverdue) || 0);
  const noodParsed = Number(nood) || 0;
  const odudNum = Number(odud);
  const odudParsed = Number.isFinite(odudNum) ? odudNum : 0;
  const overdueDays = hasOverdueDaysRestriction(noodParsed, odudParsed);
  const exposure = getCreditExposureFromState(outstanding, orderValue, creditLimit3);
  if (overdueDays && exposure.exceedsLimit) {
    return {
      kind: 'both',
      totalOverdue: overdueParsed,
      nood: noodParsed,
      odud: odudParsed,
      exposure
    };
  }
  if (overdueDays) {
    return {
      kind: 'overdue',
      totalOverdue: overdueParsed,
      nood: noodParsed,
      odud: odudParsed,
      exposure
    };
  }
  if (exposure.exceedsLimit) {
    return {
      kind: 'exposure',
      totalOverdue: overdueParsed,
      nood: noodParsed,
      odud: odudParsed,
      exposure
    };
  }
  return {
    kind: 'none',
    totalOverdue: overdueParsed,
    nood: noodParsed,
    odud: odudParsed,
    exposure
  };
}

export function resolveCreditRestrictionFromGenerateOutput(data: any): CreditRestrictionResult {
  const totalOverdueRaw = data?.totalOverdue ?? data?.customerDetails?.totalOverdue ?? 0;
  const noodRaw = data?.nood ?? data?.customerDetails?.nood ?? 0;
  const odudRaw = data?.odud ?? data?.customerDetails?.odud ?? 0;
  const { outstanding, orderValue, creditLimit3 } = resolveCreditFieldsFromGenerateOutput(data);
  return resolveCreditRestrictionFromState(
    outstanding,
    orderValue,
    creditLimit3,
    noodRaw,
    odudRaw,
    totalOverdueRaw
  );
}

/** Whether a restriction should show block/warn UI. */
export function isCreditRestrictionActive(restriction: CreditRestrictionResult): boolean {
  return restriction.kind === 'overdue' || restriction.kind === 'exposure' || restriction.kind === 'both';
}

/** Page-level credit decision after generateOutput (picklist / invoice preview). */
export function resolveCreditLimitPageAction(
  data: any,
  divisionId: string,
  storage?: { get: (key: string) => unknown } | null,
  bypassAction: CreditLimitBypassAction = 'picklist'
): { action: CreditLimitPageAction; restriction: CreditRestrictionResult | null } {
  if (
    shouldSkipCreditLimitProcessingGate(
      resolveReferenceOrderIdFromGenerateOutput(data),
      data?.invoiceDetails ?? data?.customerDetails ?? data
    ) ||
    isCreditLimitApprovedOnOrder(data) ||
    isCreditLimitApprovedOnOrder(data?.invoiceDetails) ||
    isCreditLimitApprovedOnOrder(data?.orderHeader)
  ) {
    return { action: 'none', restriction: null };
  }
  if (!isCreditLimitCheckEnabledForDivision(divisionId)) {
    return { action: 'none', restriction: null };
  }
  const restriction = resolveCreditRestrictionFromGenerateOutput(data);
  if (!isCreditRestrictionActive(restriction)) {
    return { action: 'none', restriction };
  }
  if (isCreditLimitSoftBypassUser(resolveUserEmail(storage), divisionId, bypassAction)) {
    return { action: 'proceed', restriction };
  }
  return { action: 'block', restriction };
}

export function toCreditLimitDialogDataFromExposure(exposure: CreditExposureResult): CreditLimitDialogData {
  return {
    outstanding: exposure.outstanding,
    orderValue: exposure.orderValue,
    exposureTotal: exposure.exposureTotal,
    creditLimit: exposure.creditLimit3,
    exceedBy: exposure.exceedBy
  };
}

/** Wording for Credit Limit 4 (overdue days) dialog footers. */
export function formatOverdueRestrictionNote(nood: number, odud: number, suffix = ''): string {
  const base =
    `Customer has ${nood} overdue day(s), which meets or exceeds the Credit Limit 4 threshold of ${odud} day(s).`;
  return suffix ? `${base} ${suffix}` : base;
}

/** Wording for credit-limit dialog footers (outstanding + order value vs limit). */
export function formatCreditLimitExposureNote(data: CreditLimitDialogData, suffix = ''): string {
  const base =
    `Outstanding ${data.outstanding.toFixed(2)} plus order value ${data.orderValue.toFixed(2)} ` +
    `(total ${data.exposureTotal.toFixed(2)}) is ${data.exceedBy.toFixed(2)} above the credit limit of ${data.creditLimit.toFixed(2)}.`;
  return suffix ? `${base} ${suffix}` : base;
}

/** Dialog footer: CL4, CL3, or both. Suffix is appended once at the end. */
export function formatCreditLimitDialogNote(
  kind: CreditLimitDialogKind,
  nood: number,
  odud: number,
  data: CreditLimitDialogData | null,
  suffix = '',
  trailing = ''
): string {
  const parts: string[] = [];
  if (kind === 'overdue' || kind === 'both') {
    parts.push(formatOverdueRestrictionNote(nood, odud));
  }
  if ((kind === 'exposure' || kind === 'both') && data) {
    parts.push(formatCreditLimitExposureNote(data));
  }
  let text = parts.join(' ');
  if (suffix) {
    text = text ? `${text} ${suffix}` : suffix;
  }
  if (trailing) {
    text = `${text} ${trailing}`.trim();
  }
  return text;
}

export function mapCreditRestrictionToDialog(restriction: CreditRestrictionResult): {
  kind: CreditLimitDialogKind;
  nood: number;
  odud: number;
  data: CreditLimitDialogData | null;
} {
  const kind: CreditLimitDialogKind =
    restriction.kind === 'both' || restriction.kind === 'overdue' || restriction.kind === 'exposure'
      ? restriction.kind
      : 'exposure';
  return {
    kind,
    nood: kind === 'overdue' || kind === 'both' ? restriction.nood : 0,
    odud: kind === 'overdue' || kind === 'both' ? restriction.odud : 0,
    data:
      (kind === 'exposure' || kind === 'both') && restriction.exposure
        ? toCreditLimitDialogDataFromExposure(restriction.exposure)
        : null
  };
}

/** @deprecated Use formatCreditLimitExposureNote */
export function formatCreditLimitOverdueNote(data: CreditLimitDialogData, suffix = ''): string {
  return formatCreditLimitExposureNote(data, suffix);
}

/** Debug why soft bypass did or did not apply (check browser console). */
export function logCreditLimitBypassDebug(
  tag: string,
  divisionId: string,
  storage?: { get: (key: string) => unknown } | null,
  bypassAction: CreditLimitBypassAction = 'picklist'
): void {
  if (!environment.enableDebug) {
    return;
  }
  const resolvedEmail = resolveUserEmail(storage);
  const creditCheckEnabled = isCreditLimitCheckEnabledForDivision(divisionId);
  const softBypass = isCreditLimitSoftBypassUser(resolvedEmail, divisionId, bypassAction);
  const allowlistRaw = getCreditLimitSoftBypassAllowlist(bypassAction, divisionId).join(',');
  const lsEmail =
    typeof localStorage !== 'undefined' ? localStorage.getItem('email') : null;
  const lsUserProfile =
    typeof localStorage !== 'undefined' ? localStorage.getItem('userProfile') : null;

  console.warn('[CreditLimitBypass]', tag, {
    bypassAction,
    divisionId,
    creditCheckEnabled,
    localStorageEmail: lsEmail,
    localStorageUserProfileSnippet:
      lsUserProfile && lsUserProfile.length > 120
        ? `${lsUserProfile.slice(0, 120)}...`
        : lsUserProfile,
    resolvedEmail: resolvedEmail || '(empty)',
    allowlist: allowlistRaw || '(not set on environment)',
    softBypassActive: softBypass,
    expectedDialog: softBypass ? 'warn+Continue' : 'blocked OK-only'
  });
}

/** Division 300 + allowlisted email for this action: warn popup with Continue (not hard block). */
export function isCreditLimitSoftBypassUser(
  userEmail: string | null | undefined,
  divisionId: string,
  bypassAction: CreditLimitBypassAction = 'picklist'
): boolean {
  if (!isCreditLimitCheckEnabledForDivision(divisionId)) {
    return false;
  }
  const normalized = normalizeEmail(userEmail ?? '');
  if (!normalized) {
    return false;
  }
  return getCreditLimitSoftBypassAllowlist(bypassAction, divisionId).includes(normalized);
}

export function resolveReferenceOrderIdFromGenerateOutput(data: any): string {
  return data?.invoiceDetails?.referenceOrderId ?? data?.referenceOrderId ?? '';
}

export function hasReferenceOrderId(referenceOrderId: unknown): boolean {
  const id = String(referenceOrderId ?? '').trim();
  return id !== '' && id.toLowerCase() !== 'null';
}

export function shouldSkipCreditLimitCheck(referenceOrderId: unknown): boolean {
  return hasReferenceOrderId(referenceOrderId);
}

/** True when order already has a stored credit-limit approval (point 3 audit). */
export function isCreditLimitApprovedOnOrder(source: {
  creditLimitApproved?: unknown;
} | null | undefined): boolean {
  const v = source?.creditLimitApproved;
  return v === true || v === 1 || v === '1' || String(v ?? '').trim().toLowerCase() === 'true';
}

/** Skip Sync/Invoice credit gate when already synced to M3 or credit already approved. */
export function shouldSkipCreditLimitProcessingGate(
  referenceOrderId: unknown,
  source?: { creditLimitApproved?: unknown; referenceOrderId?: unknown } | null
): boolean {
  if (shouldSkipCreditLimitCheck(referenceOrderId ?? source?.referenceOrderId)) {
    return true;
  }
  return isCreditLimitApprovedOnOrder(source);
}

/**
 * Over CL3/CL4 draft save: set M3 BLCD=2 for all users (including listed approvers).
 * Skip when env flag is off, other divisions, already approved, or already synced.
 */
export function shouldSetCustomerCreditBlockOnDraftSave(params: {
  divisionId: string;
  referenceOrderId?: unknown;
  creditApprovedSource?: { creditLimitApproved?: unknown; referenceOrderId?: unknown } | null;
}): boolean {
  if (!environment.enableCustomerCreditBlock) {
    return false;
  }
  const divisionId = String(params.divisionId ?? '').trim();
  if (!isCreditLimitCheckEnabledForDivision(divisionId)) {
    return false;
  }
  if (shouldSkipCreditLimitProcessingGate(params.referenceOrderId, params.creditApprovedSource)) {
    return false;
  }
  return true;
}

/** @deprecated Sync path no longer sets BLCD; use shouldSetCustomerCreditBlockOnDraftSave for save. */
export function shouldSetCustomerCreditBlockOnSyncHardBlock(params: {
  divisionId: string;
  userEmail?: string | null;
  referenceOrderId?: unknown;
  creditApprovedSource?: { creditLimitApproved?: unknown; referenceOrderId?: unknown } | null;
}): boolean {
  return shouldSetCustomerCreditBlockOnDraftSave(params);
}

export const CREDIT_LIMIT_APPROVAL_REASON_MIN_LENGTH = 5;

/** Order value for CL3 exposure: prefer summary, else header, else sum of line amounts. */
function parseOrderValueFromGenerateOutput(data: any): number {
  const raw =
    data?.summaryLevelData?.orderValue ??
    data?.orderValue ??
    data?.invoiceDetails?.orderValue;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) {
    return round2(n);
  }
  const lines = data?.lineDetails;
  if (Array.isArray(lines) && lines.length > 0) {
    let sum = 0;
    for (const line of lines) {
      const lineAmt = Number(
        line?.netAmount ?? line?.lineAmount ?? line?.grossAmount ?? 0
      );
      if (Number.isFinite(lineAmt)) {
        sum += lineAmt;
      }
    }
    if (sum > 0) {
      return round2(sum);
    }
  }
  return Number.isFinite(n) && n >= 0 ? round2(n) : 0;
}

export function resolveCreditFieldsFromGenerateOutput(data: any): {
  outstanding: number;
  orderValue: number;
  creditLimit3: number;
} {
  const outstandingRaw = data?.outstanding ?? data?.customerDetails?.outstanding ?? 0;
  const creditLimit3 = resolveCreditLimit3({
    okcrl3: data?.okcrl3 ?? data?.customerDetails?.okcrl3,
    creditLimit3: data?.creditLimit3 ?? data?.customerDetails?.creditLimit3
  });
  return {
    outstanding: Number(outstandingRaw) || 0,
    orderValue: parseOrderValueFromGenerateOutput(data),
    creditLimit3
  };
}

/** CL3: block when (outstanding + current order value) exceeds credit limit. */
export function exceedsCreditLimit(
  outstanding: unknown,
  orderValue: unknown,
  creditLimit3: unknown
): boolean {
  const exposure = (Number(outstanding) || 0) + (Number(orderValue) || 0);
  const limit = Number(creditLimit3) || 0;
  return limit > 0 && exposure > limit;
}

export function getCreditExposureFromState(
  outstanding: unknown,
  orderValue: unknown,
  creditLimit3: unknown
): CreditExposureResult {
  const outstandingParsed = round2(Number(outstanding) || 0);
  const orderValueParsed = round2(Number(orderValue) || 0);
  const exposureTotal = round2(outstandingParsed + orderValueParsed);
  const limitNum = Number(creditLimit3);
  const limitParsed = Number.isFinite(limitNum) && limitNum >= 0 ? limitNum : 0;
  const exceedsLimit = exceedsCreditLimit(outstandingParsed, orderValueParsed, limitParsed);
  const exceedBy = exceedsLimit ? round2(Math.max(0, exposureTotal - limitParsed)) : 0;

  return {
    outstanding: outstandingParsed,
    orderValue: orderValueParsed,
    exposureTotal,
    creditLimit3: limitParsed,
    exceedBy,
    exceedsLimit
  };
}

export function getCreditExposureFromGenerateOutput(data: any): CreditExposureResult {
  const { outstanding, orderValue, creditLimit3 } = resolveCreditFieldsFromGenerateOutput(data);
  return getCreditExposureFromState(outstanding, orderValue, creditLimit3);
}

export function isHighCreditExposure(
  outstanding: unknown,
  orderValue: unknown,
  creditLimit3: unknown
): boolean {
  return exceedsCreditLimit(outstanding, orderValue, creditLimit3);
}

/** @deprecated Use isHighCreditExposure / exceedsCreditLimit */
export function isHighOverdue(totalOverdue: unknown, creditLimit3: unknown): boolean {
  return exceedsCreditLimit(totalOverdue, 0, creditLimit3);
}

/** @deprecated Use exceedsCreditLimit */
export function exceedsCreditLimit3(totalOverdue: unknown, creditLimit3: unknown): boolean {
  return exceedsCreditLimit(totalOverdue, 0, creditLimit3);
}

export function getCreditNoteOrderTypesForDivision(division: string): string[] {
  return [...creditNoteOrderTypesForDivision(division)];
}

/** First configured credit-note type for the division (backward compatible). */
export function getCreditNoteOrderTypeForDivision(division: string): string | null {
  const types = creditNoteOrderTypesForDivision(division);
  return types[0] ?? null;
}

export function resolveOrderTypeCode(orderTypeId?: string, orderTypeDisplay?: string): string {
  const id = String(orderTypeId || '').trim();
  if (id) return id.toUpperCase();
  const display = String(orderTypeDisplay || '').trim();
  return display.split(/[\s-]/)[0].toUpperCase();
}

export function isCreditNoteOrder(division: string, orderTypeId: string): boolean {
  const expected = creditNoteOrderTypesForDivision(division);
  if (!expected.length) return false;
  const code = resolveOrderTypeCode(orderTypeId, '');
  return expected.includes(code);
}

/** Credit note lines always store negative quantity (user may type + or -). */
export function normalizeCreditNoteQuantity(qty: number): number {
  const n = Number(qty);
  if (!Number.isFinite(n) || n === 0) return n;
  return -Math.abs(n);
}

const QTY_DECIMAL_PLACES_CAP = 4;

/** Read NO. DEC PL from item details payload (supports multiple API field names). */
export function extractNoDecPl(source: any): unknown {
  if (!source) return undefined;
  return (
    source.noDecPl ??
    source['NO. DEC PL'] ??
    source.NO_DEC_PL ??
    source.decPl ??
    source.DECPL
  );
}

/** Normalize API value to effective qty decimal count (0–4); uses fallback when missing/invalid. */
export function resolveQtyDecimalPlaces(raw: unknown, fallback = 2): number {
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return fallback;
  }
  return Math.min(parsed, QTY_DECIMAL_PLACES_CAP);
}

/**
 * Normalize typed decimal input for DE/NL keyboards: treat `,` as `.`.
 * Keeps digits, one leading `-`, and at most one decimal point. No thousand separators.
 */
export function normalizeDecimalInputString(raw: string): string {
  if (raw === null || raw === undefined) {
    return '';
  }
  let s = String(raw).trim().replace(/,/g, '.');
  if (!s) {
    return '';
  }
  const negative = s.startsWith('-');
  if (negative) {
    s = s.slice(1);
  }
  s = s.replace(/[^\d.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot >= 0) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  if (negative && s !== '') {
    s = '-' + s;
  } else if (negative && s === '') {
    s = '-';
  }
  return s;
}

/** Parse a locale-friendly decimal string (comma or dot) to a finite number; invalid → 0. */
export function parseLocaleDecimal(raw: string | number | null | undefined): number {
  if (raw === null || raw === undefined || raw === '') {
    return 0;
  }
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : 0;
  }
  const normalized = normalizeDecimalInputString(String(raw));
  if (!normalized || normalized === '-' || normalized === '.') {
    return 0;
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

/** Count fractional digits from string input (preferred) or number. */
export function countDecimalPlaces(value: unknown): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  if (typeof value === 'string') {
    const trimmed = normalizeDecimalInputString(value);
    if (!trimmed || trimmed === '-' || trimmed === '+') return 0;
    const parts = trimmed.split('.');
    return parts.length > 1 ? parts[1].length : 0;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const parts = n.toString().split('.');
  return parts.length > 1 ? parts[1].length : 0;
}

export function isQuantityAllowedByDecimalPlaces(qty: number | string, allowed: number): boolean {
  return countDecimalPlaces(qty) <= allowed;
}

/** Resolve noDecPl for an order line: explicit API value, else infer from qty precision, else fallback. */
export function resolveLineNoDecPl(item: any, fallback = 2): number {
  const explicit = item?.noDecPl ?? extractNoDecPl(item);
  if (explicit !== undefined && explicit !== null && explicit !== '') {
    return resolveQtyDecimalPlaces(explicit, fallback);
  }
  const qty = item?.qty ?? item?.quantity;
  const decimals = countDecimalPlaces(qty ?? 0);
  if (decimals > 0) {
    return resolveQtyDecimalPlaces(decimals, fallback);
  }
  return fallback;
}

/** Angular number-pipe format for qty display (min 0 fraction digits, max = allowed or stored precision). */
export function qtyDisplayFormat(noDecPl: unknown, qty?: unknown, fallback = 2): string {
  const allowed = resolveQtyDecimalPlaces(noDecPl, fallback);
  const actual = countDecimalPlaces(qty ?? 0);
  const decimals = Math.min(Math.max(allowed, actual), QTY_DECIMAL_PLACES_CAP);
  return `1.0-${decimals}`;
}

/** Truncate typed decimal input; when allowed is 0, strip the decimal point entirely. Accepts `,` as decimal. */
export function restrictDecimalInputValue(value: string, maxDecimals: number): string {
  const normalized = normalizeDecimalInputString(String(value ?? ''));
  if (!normalized || !normalized.includes('.')) {
    return normalized;
  }
  if (maxDecimals <= 0) {
    return normalized.split('.')[0];
  }
  const [integer, decimal = ''] = normalized.split('.');
  if (decimal.length <= maxDecimals) {
    return normalized;
  }
  return integer + '.' + decimal.substring(0, maxDecimals);
}

/**
 * Rounds to 2 decimals, half up (e.g. 1.845 → 1.85).
 * Use for all amount/VAT fields to avoid float giving 1.84.
 */
export function round2(value: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/**
 * Clamps discount percentage to [0, 100] with at most 2 decimal places.
 * Treats null, undefined, '', NaN as 0.
 */
export function clampDiscountPercent(value: any): number {
  const n = Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(n)) return 0;
  return round2(Math.max(0, Math.min(100, n)));
}

/** Parse M3 status string/number; returns null when missing or non-numeric. */
export function parseM3Status(value: any): number | null {
  if (value === null || value === undefined || String(value).trim() === '') {
    return null;
  }
  const status = Number(String(value).trim());
  return Number.isFinite(status) ? status : null;
}

/**
 * Order-level gate from GetOrderHeader: both m3LowestStatus and m3HighestStatus must be present and &lt; 66.
 * highest &lt; 66 implies every line is below 66 (covers cancelled/high statuses via highest).
 */
export function canEditPricesFromOrderHeader(header: any): boolean {
  const lowest = parseM3Status(header?.m3LowestStatus);
  const highest = parseM3Status(header?.m3HighestStatus);
  if (lowest === null || highest === null) {
    return false;
  }
  return lowest < 66 && highest < 66;
}

/** True when synced line may edit rate/discount for M3 UpdPriceInfo (status strictly before 66). */
export function canEditPriceInM3(
  row: any,
  opts?: { hasReferenceOrder?: boolean; roleReadOnly?: boolean; orderPriceEditable?: boolean }
): boolean {
  if (opts?.roleReadOnly) {
    return false;
  }
  const hasRef =
    opts?.hasReferenceOrder === true ||
    !!(row?.referenceOrderNumber || row?.referenceOrderId);
  if (!hasRef) {
    return false;
  }
  const status = parseM3Status(row?.m3LineStatus);
  if (status !== null) {
    if (status === 99 || status >= 66) {
      return false;
    }
    return status < 66;
  }
  // Line status missing: allow only when order header already approved both statuses &lt; 66.
  return opts?.orderPriceEditable === true;
}

export function isPriceDirtyVsBaseline(row: any): boolean {
  const rate = round2(Number(row?.rate) || 0);
  const discount = clampDiscountPercent(row?.discount);
  const baseRate = round2(Number(row?.baselineRate ?? row?.rate) || 0);
  const baseDisc = clampDiscountPercent(row?.baselineDiscount ?? row?.discount);
  return rate !== baseRate || discount !== baseDisc;
}

export function seedPriceBaselines(lines: any[]): void {
  for (const row of lines || []) {
    if (!row) continue;
    row.baselineRate = round2(Number(row.rate) || 0);
    row.baselineDiscount = clampDiscountPercent(row.discount);
    if (!row.priceUpdateStatus) {
      row.priceUpdateStatus = 'idle';
    }
  }
}

/**
 * France short-pick: when shipQty is persisted use it for money/weight math;
 * otherwise fall back to ordered qty (quantity / qty).
 * shipQty === 0 is a valid shipped quantity (not a missing value).
 */
export function effectiveShippedQty(line: any): number {
  if (line == null) {
    return 0;
  }
  if (line.shipQty !== null && line.shipQty !== undefined && line.shipQty !== '') {
    const shipped = Number(line.shipQty);
    if (Number.isFinite(shipped)) {
      return shipped;
    }
  }
  const ordered = Number(line.quantity ?? line.qty);
  return Number.isFinite(ordered) ? ordered : 0;
}

/** True when France Process persisted ship_qty (locks Ordered edits on V2). */
export function hasPersistedShipQty(line: any): boolean {
  if (line == null) {
    return false;
  }
  if (line.shipQty === null || line.shipQty === undefined || line.shipQty === '') {
    return false;
  }
  return Number.isFinite(Number(line.shipQty));
}

/** Map API/DTO ship fields onto a UI row: shipQty + shippedQuantity (= COALESCE). */
export function mapShipQtyFields(item: any): { shipQty: number | null; shippedQuantity: number } {
  const ordered = Number(item?.qty ?? item?.quantity) || 0;
  const raw = item?.shipQty ?? item?.ship_qty;
  if (raw === null || raw === undefined || raw === '') {
    return { shipQty: null, shippedQuantity: ordered };
  }
  const shipQty = Number(raw);
  if (!Number.isFinite(shipQty)) {
    return { shipQty: null, shippedQuantity: ordered };
  }
  return { shipQty, shippedQuantity: shipQty };
}

/** Keep shippedQuantity in sync when Ordered changes and shipQty is not persisted. */
export function syncShippedQuantityFromOrdered(line: any): void {
  if (!line) {
    return;
  }
  if (hasPersistedShipQty(line)) {
    line.shippedQuantity = Number(line.shipQty);
    return;
  }
  line.shippedQuantity = Number(line.quantity ?? line.qty) || 0;
}

/**
 * Calculates all amounts for a row: gross, discount, net, VAT, and final.
 * Uses effective shipped qty (COALESCE(shipQty, ordered)) so short-picked lines
 * calculate on shipped while Ordered (quantity) stays unchanged.
 */
export function calculateRowAmounts(row: any): any {
  const quantity = effectiveShippedQty(row);
  const rate = Number(row.rate) || 0;
  const discount = clampDiscountPercent(row.discount);
  const vat = Number(row.vat) || 0;

  const amount = round2(quantity * rate);
  const discountAmount = round2((amount * discount) / 100);
  const amountAfterDiscount = round2(amount - discountAmount);
  const vatAmount = round2((amountAfterDiscount * vat) / 100);
  const amountAfterVat = round2(amountAfterDiscount + vatAmount);

  return {
    ...row,
    shippedQuantity: quantity,
    amount,
    discount,
    discountAmount,
    amountAfterDiscount,
    vat,
    vatAmount,
    amountAfterVat
  };
}

/**
 * Calculates VAT amount and amount after VAT.
 * @param amountAfterDiscount - The amount after discount.
 * @param vat - The VAT percentage.
 * @returns Object with vatAmount and amountAfterVat.
 */
export function calculateVatFields(amountAfterDiscount: number, vat: number) {
  const amt = round2(Number(amountAfterDiscount) || 0);
  const vatVal = Number(vat) || 0;
  const vatAmount = round2((amt * vatVal) / 100);
  const amountAfterVat = round2(amt + vatAmount);
  return { vatAmount, amountAfterVat };
}

/**
 * Calculates discount amount and amount after discount.
 * @param amount - The gross amount.
 * @param discount - The discount percentage.
 * @returns Object with discountAmount and amountAfterDiscount.
 */
export function calculateDiscountFields(amount: number, discount: number) {
  const amt = round2(Number(amount) || 0);
  const disc = clampDiscountPercent(discount);
  const discountAmount = round2((amt * disc) / 100);
  const amountAfterDiscount = Math.max(0, round2(amt - discountAmount));
  return { discountAmount, amountAfterDiscount };
}

export interface DivisionMarginResolution {
  division: string;
  selectedPriceList: string;
  costField: string;
  marginField: string;
  cost: number;
  minimumMargin: number;
  rawValues: {
    C1APPR?: string | number;
    C2APPR?: string | number;
    C3APPR?: string | number;
    MFLAMA?: string | number;
    MILAMA?: string | number;
    MTLAMA?: string | number;
  };
  source: 'lstPriceList' | 'ItemDetails' | 'none';
}

export function normalizeDivisionCode(division: string | number | null | undefined): string {
  const d = String(division ?? '').trim();
  const match = d.match(/^(100|300|400)/);
  return match ? match[1] : d;
}

export function divisionPriceListSeriesFallback(division: string): string[] {
  const d = normalizeDivisionCode(division);
  if (d === '300') {
    return ['FR1', 'FR2', 'FR3'];
  }
  if (d === '400') {
    return ['NL1', 'NL2', 'NL3'];
  }
  return ['PL1', 'PL2', 'PL3'];
}

export function selectPriceListRow(
  rows: any[] | null | undefined,
  division: string,
  customerDefaultPriceList?: string | null
): any | null {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }
  const target = (customerDefaultPriceList || '').trim();
  if (target) {
    const found = rows.find(
      (row) => String(row?.ODPRRF ?? '').trim().toUpperCase() === target.toUpperCase()
    );
    if (found) {
      return found;
    }
  }
  for (const code of divisionPriceListSeriesFallback(division)) {
    const found = rows.find(
      (row) => String(row?.ODPRRF ?? '').trim().toUpperCase() === code.toUpperCase()
    );
    if (found) {
      return found;
    }
  }
  return rows[0];
}

export function resolveCostFieldByDivision(division: string): string {
  const d = normalizeDivisionCode(division);
  if (d === '300') {
    return 'C2APPR';
  }
  if (d === '400') {
    return 'C3APPR';
  }
  return 'C1APPR';
}

export function resolveMarginFieldByDivision(division: string): string {
  const d = normalizeDivisionCode(division);
  if (d === '300') {
    return 'MILAMA';
  }
  if (d === '400') {
    return 'MTLAMA';
  }
  return 'MFLAMA';
}

export function resolveDivisionMarginFromPriceListRow(
  priceListRow: any,
  division: string
): { cost: number; minimumMargin: number; costField: string; marginField: string } {
  const costField = resolveCostFieldByDivision(division);
  const marginField = resolveMarginFieldByDivision(division);
  return {
    costField,
    marginField,
    cost: Number(priceListRow?.[costField]) || 0,
    minimumMargin: Number(priceListRow?.[marginField]) || 0
  };
}

/**
 * Prefer division cost/MCM (e.g. FR C2APPR/MILAMA). When those are 0/missing,
 * fall back to ItemDetails MCCSU1 / MFLAMA so copy/excel/save do not keep
 * re-fetching forever on empty division fields.
 */
function coalescePositive(...values: unknown[]): number {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return 0;
}

export function resolveDivisionMarginFromItemDetailsResponse(
  response: any,
  division: string,
  customerDefaultPriceList?: string | null
): DivisionMarginResolution | null {
  const normalizedDivision = normalizeDivisionCode(division);
  const priceListRow = selectPriceListRow(response?.lstPriceList, normalizedDivision, customerDefaultPriceList);
  const itemDetails = response?.ItemDetails;
  const costField = resolveCostFieldByDivision(normalizedDivision);
  const marginField = resolveMarginFieldByDivision(normalizedDivision);

  if (priceListRow) {
    const fromRow = resolveDivisionMarginFromPriceListRow(priceListRow, normalizedDivision);
    const cost = coalescePositive(
      fromRow.cost,
      itemDetails?.[costField],
      itemDetails?.MCCSU1
    );
    const minimumMargin = coalescePositive(
      fromRow.minimumMargin,
      itemDetails?.[marginField],
      itemDetails?.MFLAMA
    );
    return {
      division: normalizedDivision,
      selectedPriceList: String(priceListRow?.ODPRRF ?? '').trim(),
      costField,
      marginField,
      cost,
      minimumMargin,
      rawValues: {
        C1APPR: priceListRow?.C1APPR,
        C2APPR: priceListRow?.C2APPR,
        C3APPR: priceListRow?.C3APPR,
        MFLAMA: priceListRow?.MFLAMA,
        MILAMA: priceListRow?.MILAMA,
        MTLAMA: priceListRow?.MTLAMA
      },
      source: 'lstPriceList'
    };
  }

  if (!itemDetails) {
    return null;
  }

  return {
    division: normalizedDivision,
    selectedPriceList: String(itemDetails?.selectedPriceList ?? '').trim(),
    costField,
    marginField,
    cost: coalescePositive(itemDetails[costField], itemDetails.MCCSU1),
    minimumMargin: coalescePositive(itemDetails[marginField], itemDetails.MFLAMA),
    rawValues: {
      C1APPR: itemDetails.C1APPR,
      C2APPR: itemDetails.C2APPR,
      C3APPR: itemDetails.C3APPR,
      MFLAMA: itemDetails.MFLAMA,
      MILAMA: itemDetails.MILAMA,
      MTLAMA: itemDetails.MTLAMA
    },
    source: 'ItemDetails'
  };
}

/** Apply ItemDetails cost/MCM onto a line; keeps prior positive values if API still returns 0. */
export function applyMarginFromItemDetailsToLine(
  line: any,
  response: any,
  division: string,
  customerDefaultPriceList?: string | null,
  qtyForLineCost?: number
): DivisionMarginResolution | null {
  if (!line) {
    return null;
  }
  const priorCost = Number(line.cost ?? line.costPrice) || 0;
  const priorMargin = Number(line.minimumMargin ?? line.itemMcm) || 0;
  const resolution = resolveDivisionMarginFromItemDetailsResponse(
    response,
    division,
    customerDefaultPriceList
  );
  const qty = qtyForLineCost != null ? Number(qtyForLineCost) : Number(line.quantity ?? 0);
  if (resolution) {
    line.cost = resolution.cost > 0 ? resolution.cost : priorCost;
    line.minimumMargin = resolution.minimumMargin > 0 ? resolution.minimumMargin : priorMargin;
  } else if (response?.ItemDetails) {
    const fallbackCost = coalescePositive(response.ItemDetails.MCCSU1, priorCost);
    const fallbackMargin = coalescePositive(response.ItemDetails.MFLAMA, priorMargin);
    line.cost = fallbackCost;
    line.minimumMargin = fallbackMargin;
  }
  const unitCost = Number(line.cost) || 0;
  if (Number.isFinite(qty)) {
    line.lineCost = round2(unitCost * qty);
  }
  line.marginDataLoaded = true;
  return resolution;
}

export function logDivisionMarginResolution(context: string, resolution: DivisionMarginResolution | null): void {
  if (!resolution) {
    console.warn(`[MARGIN-DIVISION] ${context} — no cost/margin resolved`);
    return;
  }
  console.log(`[MARGIN-DIVISION] ${context}`, {
    division: resolution.division,
    selectedPriceList: resolution.selectedPriceList,
    costField: resolution.costField,
    marginField: resolution.marginField,
    resolvedCost: resolution.cost,
    resolvedMinimumMargin: resolution.minimumMargin,
    rawPriceListValues: resolution.rawValues,
    source: resolution.source
  });
}

/** Maps order warehouse to the single M3 stock field used in F4 browse. */
export function resolveWarehouseStockField(warehouse: string | number | null | undefined): string | null {
  const code = ('' + (warehouse ?? '')).trim();
  switch (code) {
    case '101':
      return 'V_AVA5';
    case '110':
      return 'V_AVA3';
    case '130':
      return 'V_AVA6';
    case '300':
      return 'V_AVA4';
    case '400':
      return 'V_AVA7';
    default:
      return null;
  }
}

/**
 * Local stock to subtract for display: unsaved (status 10) and edited (status 30) lines
 * on the current order. Saved lines (status 20) are already counted in backend reserved qty.
 */
export function calculateLocalStockForDisplay(
  itemList: any[],
  itemNumber: string,
  currentRow?: any
): number {
  let totalToSubtract = 0;
  const allItems = (itemList || []).filter((item) => item.itemNumber === itemNumber);

  allItems.forEach((item) => {
    if (item.status === 10) {
      totalToSubtract += Number(item.quantity) || 0;
    } else if (item.status === 30) {
      const currentQty = Number(item.quantity) || 0;
      const originalQty =
        item.originalQuantity !== undefined ? Number(item.originalQuantity) || 0 : currentQty;
      const originalStatus = item.originalStatus;

      if (originalStatus === 20 || item.originalQuantityWasSaved === true) {
        totalToSubtract += currentQty - originalQty;
      } else {
        totalToSubtract += currentQty;
      }
    }
  });

  return totalToSubtract;
}

/** Match customer default price list against M3 itemdetails lstPriceList (ODPRRF / ODSAPR). */
export function resolveRateFromItemDetailsResponse(response: any, defaultPriceList: string): number {
  const lists = response?.lstPriceList;
  if (!Array.isArray(lists) || lists.length === 0) {
    return 0;
  }
  const dpl = String(defaultPriceList || '').trim();
  if (dpl) {
    const match = lists.find(
      (pl: { ODPRRF?: string }) => String(pl?.ODPRRF || '').trim().toUpperCase() === dpl.toUpperCase()
    );
    if (match != null && match.ODSAPR != null && match.ODSAPR !== '') {
      return Number(Number(match.ODSAPR).toFixed(2));
    }
  }
  const first = lists[0];
  if (first?.ODSAPR != null && first.ODSAPR !== '') {
    return Number(Number(first.ODSAPR).toFixed(2));
  }
  return 0;
}

export interface QuotationPunchLineEnrichmentCtx {
  defaultPriceList: string;
  shouldFetchVatPercentage: boolean;
}

function applyQuotationStockFromItemDetails(line: any, response: any | null): void {
  const itemStockCount = response?.ItemDetails?.itemStockCount;
  const count = itemStockCount !== undefined && itemStockCount !== null ? Number(itemStockCount) : null;
  if (count === null || isNaN(count)) {
    line.copyOrderStockError = 'Item or stock could not be verified. Reduce quantity or delete line.';
    line.avlStock = 0;
  } else if (count < line.quantity) {
    line.copyOrderStockError = `Insufficient stock. Available: ${count}. Reduce quantity or delete line.`;
    line.avlStock = count;
    line.apiStock = count;
  } else {
    line.avlStock = count;
    line.apiStock = count;
  }
}

/** Stock, rate (if zero), VAT, and row totals for quotation punch lines from itemdetails response. */
export function applyQuotationPunchLineFromItemDetails(
  line: any,
  response: any | null,
  ctx: QuotationPunchLineEnrichmentCtx
): any {
  applyQuotationStockFromItemDetails(line, response);
  if (response) {
    const currentRate = Number(line.rate) || 0;
    if (currentRate <= 0) {
      const resolvedRate = resolveRateFromItemDetailsResponse(response, ctx.defaultPriceList);
      if (resolvedRate > 0) {
        line.rate = resolvedRate;
      }
    }
    if (ctx.shouldFetchVatPercentage) {
      const vtp1 = response?.vatFields?.VTP1;
      if (vtp1 !== undefined && vtp1 !== null && vtp1 !== '') {
        line.vat = Number(vtp1);
      }
    }
  }
  // Mutate in place — calculateRowAmounts returns a new object; callers keep the same line refs.
  return Object.assign(line, calculateRowAmounts(line));
}

/** Country code for VAT from SearchCustomer/Details addresses and selected addressId. */
export function resolveCustomerCountryCodeFromDetails(details: any, addressId?: string): string {
  const addresses = Array.isArray(details?.addresses) ? details.addresses : [];
  const id = String(addressId ?? details?.addressId ?? '').trim();
  if (id && addresses.length) {
    const match = addresses.find((a: { addressId?: string }) => String(a?.addressId ?? '').trim() === id);
    const fromMatch = String(match?.countryCode ?? '').trim();
    if (fromMatch) {
      return fromMatch;
    }
  }
  return String(addresses[0]?.countryCode ?? details?.countryCode ?? '').trim();
}